/**
 * arbi local ls [path]           — list files in a directory
 * arbi local find <pattern>      — find files matching a glob pattern
 * arbi local cat <file>          — print file contents
 * arbi local tree [path]         — show directory tree
 *
 * Local filesystem operations — allows agents to browse files
 * without requiring shell access. Each command supports `--json` so
 * an agent can parse the output cleanly; SKILL.md positions these
 * commands as AI-friendly, so they have to actually be parseable.
 */

import { Command } from 'commander'
import { readdirSync, readFileSync, statSync, globSync } from 'node:fs'
import { resolve, join, basename } from 'node:path'
import { homedir } from 'node:os'
import { printJson } from '../helpers.js'

/** Resolve ~ and relative paths */
function resolvePath(p?: string): string {
  const raw = p ?? '.'
  const expanded = raw.startsWith('~') ? raw.replace('~', homedir()) : raw
  return resolve(expanded)
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}K`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}M`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)}G`
}

/** Stat shape returned by --json paths. Public contract — keep stable. */
interface LocalEntry {
  name: string
  type: 'dir' | 'file' | 'symlink' | 'other'
  size: number | null
}

function entryToRow(dir: string, name: string): LocalEntry {
  try {
    const stat = statSync(join(dir, name))
    if (stat.isDirectory()) return { name, type: 'dir', size: null }
    if (stat.isSymbolicLink()) return { name, type: 'symlink', size: stat.size }
    if (stat.isFile()) return { name, type: 'file', size: stat.size }
    return { name, type: 'other', size: stat.size }
  } catch {
    return { name, type: 'other', size: null }
  }
}

export function registerLocalCommand(program: Command): void {
  const local = program.command('local').description('Local filesystem operations')

  // ── ls ──────────────────────────────────────────────────────────────────
  local
    .command('ls [path]')
    .description('List files in a directory')
    .option('-l, --long', 'Show file sizes and types (ignored when --json is set)')
    .option('-a, --all', 'Include dotfiles')
    .option('--json', 'Output as JSON array of {name, type, size}')
    .action((path?: string, opts?: { long?: boolean; all?: boolean; json?: boolean }) => {
      const dir = resolvePath(path)
      let entries: string[]
      try {
        entries = readdirSync(dir)
      } catch {
        console.error(`Cannot read directory: ${dir}`)
        process.exit(1)
      }

      const sorted = entries.filter((e) => opts?.all || !e.startsWith('.')).sort()

      if (opts?.json) {
        const rows: LocalEntry[] = sorted.map((name) => entryToRow(dir, name))
        printJson(rows)
        return
      }

      for (const entry of sorted) {
        if (opts?.long) {
          const row = entryToRow(dir, entry)
          const size = row.size !== null && row.type === 'file' ? formatSize(row.size) : '-'
          console.log(`${row.type.padEnd(5)} ${size.padStart(10)}  ${entry}`)
        } else {
          console.log(entry)
        }
      }
    })

  // ── find ─────────────────────────────────────────────────────────────────
  local
    .command('find <pattern>')
    .description('Find files matching a glob (e.g. "**/*.pdf")')
    .option('-d, --dir <path>', 'Directory to search in', '.')
    .option('--json', 'Output as JSON array of matched paths')
    .action((pattern: string, opts: { dir: string; json?: boolean }) => {
      const dir = resolvePath(opts.dir)
      const results = globSync(pattern, { cwd: dir })

      if (opts.json) {
        // Always print an array, even when empty — keeps `arbi local find …
        // --json | jq` honest. Status hints (the human "No files matching"
        // line) go to stderr in JSON mode so stdout is purely data.
        if (results.length === 0) {
          process.stderr.write(`No files matching "${pattern}" in ${dir}\n`)
        }
        printJson(results)
        return
      }

      if (results.length === 0) {
        process.stderr.write(`No files matching "${pattern}" in ${dir}\n`)
        return
      }

      for (const file of results) {
        console.log(file)
      }
    })

  // ── cat ──────────────────────────────────────────────────────────────────
  local
    .command('cat <file>')
    .description('Print file contents')
    .option('--head <lines>', 'Only show first N lines')
    .option(
      '--json',
      'Output as {path, bytes, content} — useful when content has newlines an agent needs to handle'
    )
    .action((file: string, opts: { head?: string; json?: boolean }) => {
      const filePath = resolvePath(file)
      let content: string
      try {
        content = readFileSync(filePath, 'utf-8')
      } catch {
        console.error(`Cannot read file: ${filePath}`)
        process.exit(1)
      }

      if (opts.head) {
        const n = parseInt(opts.head, 10)
        content = content.split('\n').slice(0, n).join('\n')
      }

      if (opts.json) {
        printJson({ path: filePath, bytes: Buffer.byteLength(content, 'utf-8'), content })
        return
      }

      console.log(content)
    })

  // ── tree ─────────────────────────────────────────────────────────────────
  local
    .command('tree [path]')
    .description('Show directory tree')
    .option('-d, --depth <n>', 'Maximum depth', '3')
    .option('--json', 'Output as nested {name, type, children} tree')
    .action((path?: string, opts?: { depth?: string; json?: boolean }) => {
      const dir = resolvePath(path)
      const maxDepth = parseInt(opts?.depth ?? '3', 10)

      if (opts?.json) {
        printJson(buildTree(dir, maxDepth, 0))
        return
      }

      console.log(basename(dir) + '/')
      printTree(dir, '', maxDepth, 0)
    })
}

interface TreeNode {
  name: string
  type: 'dir' | 'file' | 'symlink' | 'other'
  children?: TreeNode[]
}

function buildTree(dir: string, maxDepth: number, depth: number): TreeNode {
  const node: TreeNode = { name: basename(dir), type: 'dir', children: [] }
  if (depth >= maxDepth) return node
  let entries: string[]
  try {
    entries = readdirSync(dir)
      .filter((e) => !e.startsWith('.'))
      .sort()
  } catch {
    return node
  }
  for (const entry of entries) {
    const full = join(dir, entry)
    let isDir = false
    try {
      isDir = statSync(full).isDirectory()
    } catch {
      node.children!.push({ name: entry, type: 'other' })
      continue
    }
    if (isDir) {
      node.children!.push(buildTree(full, maxDepth, depth + 1))
    } else {
      node.children!.push({ name: entry, type: 'file' })
    }
  }
  return node
}

function printTree(dir: string, prefix: string, maxDepth: number, depth: number): void {
  if (depth >= maxDepth) return
  let entries: string[]
  try {
    entries = readdirSync(dir)
      .filter((e) => !e.startsWith('.'))
      .sort()
  } catch {
    return
  }

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]
    const isLast = i === entries.length - 1
    const connector = isLast ? '└── ' : '├── '
    const full = join(dir, entry)
    let isDir = false
    try {
      isDir = statSync(full).isDirectory()
    } catch {
      continue
    }
    console.log(`${prefix}${connector}${entry}${isDir ? '/' : ''}`)
    if (isDir) {
      const nextPrefix = prefix + (isLast ? '    ' : '│   ')
      printTree(full, nextPrefix, maxDepth, depth + 1)
    }
  }
}
